import React, { useContext, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { setAiRolePlayCourseAccessSetting } from 'redux/actions/ai-roleplay-usage';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { RoleplayApprovalMode } from 'redux/schemas/models/institution';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import ApprovalOption from './approval-option';

const usageManagementTags = config.pendo.activities.aiRolePlay.usageManagement;

type ManageUsageModalProps = {
  show: boolean,
  onClose: () => void,
};

const bodyStyles = css`
  .text-large-body {
    line-height: 25px;
  }
`;

const ManageUsageModal = ({ show, onClose }: ManageUsageModalProps) => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [InstitutionsManager] = injectServices(['InstitutionsManager']);

  const currentMode = InstitutionsManager.institution.aiRolePlayCourseAccessSetting
    ?? RoleplayApprovalMode.ALL_COURSES;

  const [selectedMode, setSelectedMode] = useState<RoleplayApprovalMode>(currentMode);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setSelectedMode(currentMode);
    }
  }, [show, currentMode]);

  const isDirty = selectedMode !== currentMode;

  const { ROLEPLAY_USAGE_MANAGEMENT } = t.INSTITUTIONS.ADVANCED_SETTINGS.GENERATIVE_AI_CAPABILITIES;

  const applyModeChange = () => {
    setIsSaving(true);

    return wrapThunkAction(dispatch(setAiRolePlayCourseAccessSetting({
      institutionId: InstitutionsManager.institution.id,
      setting: selectedMode,
    }))).then(() => {
      InstitutionsManager.institution.updateFromReact({ aiRolePlayCourseAccessSetting: selectedMode });
      dispatch(addAlertMessage({
        type: AlertMessageType.SUCCESS,
        header: t.FORM.SUCCESS(),
        message: ROLEPLAY_USAGE_MANAGEMENT.UPDATE_SUCCESS(),
      }));
      onClose();
    }).catch(() => {
      dispatch(addAlertMessage({
        type: AlertMessageType.ERROR,
        header: t.FORM.OOPS(),
        message: t.FORM.ERROR_SOMETHING_WRONG(),
      }));
    }).finally(() => setIsSaving(false));
  };

  const handleUpdate = () => {
    const isCourseApproval = selectedMode === RoleplayApprovalMode.COURSE_APPROVAL;

    dispatch(openConfirmationDialog({
      title: isCourseApproval
        ? ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.COURSE_APPROVAL_TITLE()
        : ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.ENABLE_ALL_TITLE(),
      bodyText: isCourseApproval
        ? ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.COURSE_APPROVAL_DESCRIPTION()
        : ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.ENABLE_ALL_DESCRIPTION(),
      confirmText: isCourseApproval
        ? ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.COURSE_APPROVAL_CONFIRM()
        : ROLEPLAY_USAGE_MANAGEMENT.CONFIRM.ENABLE_ALL_CONFIRM(),
      cancelText: t.FORM.CANCEL(),
      confirmDataQa: isCourseApproval
        ? usageManagementTags.confirmCourseApproval
        : usageManagementTags.confirmEnableAll,
      onConfirm: applyModeChange,
    }));
  };

  return (
    <NvModal
      type={ModalType.DYNAMIC}
      width={600}
      fullHeight={false}
      show={show}
      onClose={onClose}
      header={ROLEPLAY_USAGE_MANAGEMENT.TITLE()}
      body={(
        <div className='p-4' css={bodyStyles}>
          <div className='text-large-body font-weight-bold text-gray-1'>
            {ROLEPLAY_USAGE_MANAGEMENT.COURSES_TITLE()}
          </div>
          <div className='text-gray-1 mb-2'>
            {ROLEPLAY_USAGE_MANAGEMENT.COURSES_DESCRIPTION()}
          </div>
          <ApprovalOption
            mode={RoleplayApprovalMode.ALL_COURSES}
            label={ROLEPLAY_USAGE_MANAGEMENT.ALL_COURSES()}
            description={ROLEPLAY_USAGE_MANAGEMENT.ALL_COURSES_DESCRIPTION()}
            selected={selectedMode === RoleplayApprovalMode.ALL_COURSES}
            dataQa={usageManagementTags.allCourses}
            onSelect={setSelectedMode}
          />
          <div className='mt-2 mb-2'>
            <ApprovalOption
              mode={RoleplayApprovalMode.COURSE_APPROVAL}
              label={ROLEPLAY_USAGE_MANAGEMENT.COURSE_APPROVAL()}
              description={ROLEPLAY_USAGE_MANAGEMENT.COURSE_APPROVAL_DESCRIPTION()}
              selected={selectedMode === RoleplayApprovalMode.COURSE_APPROVAL}
              dataQa={usageManagementTags.courseApproval}
              onSelect={setSelectedMode}
            />
          </div>
          <div className='d-flex justify-content-center mt-8'>
            <Button variant='secondary' className='mr-2' onClick={onClose}>
              {t.FORM.CANCEL()}
            </Button>
            <Button
              variant='primary'
              disabled={!isDirty || isSaving}
              onClick={handleUpdate}
              data-qa={usageManagementTags.updateButton}
            >
              {isSaving ? t.FORM.UPDATING() : t.FORM.UPDATE()}
            </Button>
          </div>
        </div>
      )}
    />
  );
};

export default ManageUsageModal;
